import React, { useState } from "react";
import { useParams } from "react-router-dom";
import { Printer, Trash2 } from "react-feather";
import toast from "react-hot-toast";
import { useGetStockReturnQuery, useDeleteStockReturnMutation } from "../../stockApi";
import { useCanPrintReturn, useCanDeleteReturn } from "../../useStockPermissions";
import ReasonPrompt from "../../../../components/common/ReasonPrompt";
import ActorName from "../../../../components/common/ActorName";
import StockDocumentPrintModal from "../../components/print/StockDocumentPrintModal";
import BackButton from "../../../../components/common/BackButton";
import { useGetCompanyInfoQuery } from "../../../settings/settingsApi";

const StockReturnDetail = () => {
  const { recordGuid } = useParams();
  const canPrint = useCanPrintReturn();
  const canDelete = useCanDeleteReturn();
  const { data: ret, isLoading, isError, error } = useGetStockReturnQuery(recordGuid);
  const { data: companyInfo } = useGetCompanyInfoQuery(ret?.branchId, { skip: !ret });
  const [deleteStockReturn] = useDeleteStockReturnMutation();
  const [showPrint, setShowPrint] = useState(false);
  const [showDeleteForm, setShowDeleteForm] = useState(false);

  const handleDelete = async (deleteReason) => {
    try {
      await deleteStockReturn({ recordGuid, deleteReason: deleteReason || null }).unwrap();
      toast.success("Stock return deleted - stock balance reversed");
      setShowDeleteForm(false);
    } catch (e) {
      toast.error(e?.message || "Could not delete the return");
    }
  };

  if (isLoading) return <div className="page-wrapper"><div className="content"><p>Loading...</p></div></div>;
  if (isError || !ret) {
    return (
      <div className="page-wrapper">
        <div className="content">
          <BackButton className="d-inline-flex align-items-center mb-3" />
          <div className="alert alert-danger">Could not load this return ({error?.message || "server error"}).</div>
        </div>
      </div>
    );
  }

  const printProps = {
    title: "STOCK RETURN NOTE",
    docNo: ret.returnNo,
    docDate: new Date(ret.returnDate).toLocaleDateString(),
    branchLabel: ret.branchCode,
    partyLabel: ret.returnType === "STOCK_OUT_BASED" ? "Against Stock-Out" : "Supplier",
    partyName: ret.returnType === "STOCK_OUT_BASED" ? ret.stockOutNo : ret.supplierName,
    // Resolved server-side now (Batch44) for a direct-to-supplier return -
    // a Stock-Out-based return has no separate "party", it's the Stock-Out itself.
    partyAddress: ret.returnType === "DIRECT_SUPPLIER" ? ret.supplierAddress || "" : "",
    partyMobile: ret.returnType === "DIRECT_SUPPLIER" ? ret.supplierMobile || ret.supplierTelephone || "" : "",
    remarks: [ret.returnReason, ret.remarks].filter(Boolean).join(" - "),
    showVat: false,
    lines: ret.lines.map((l) => ({ qty: l.returnedQty, productName: l.productName, price: l.unitRate, amount: l.lineAmount })),
    totals: { subTotal: ret.totalAmount, vatAmount: 0, netAmount: ret.totalAmount },
    preparedByName: ret.createdByName,
    ...(companyInfo && {
      companyName: companyInfo["Company.Name"] || undefined,
      companyAddress: companyInfo["Company.Address"] || undefined,
      companyPhone: companyInfo["Company.Phone"] || undefined,
      companyEmail: companyInfo["Company.Email"] || undefined,
    }),
  };

  return (
    <div className="page-wrapper">
      <div className="content">
        <div className="page-header">
          <div className="add-item d-flex">
            <div className="page-title">
              <BackButton />
              <h4>{ret.returnNo}</h4>
              <h6>{ret.branchCode} - {ret.returnType === "STOCK_OUT_BASED" ? "Stock-Out Based" : "Direct to Supplier"}</h6>
            </div>
          </div>
          <div className="page-btn d-flex gap-2">
            {canPrint && (
              <button type="button" className="btn btn-outline-primary d-inline-flex align-items-center gap-1" onClick={() => setShowPrint(true)}>
                <Printer size={16} /> Print
              </button>
            )}
            {canDelete && !ret.isDeleted && (
              <button type="button" className="btn btn-outline-danger d-inline-flex align-items-center gap-1" onClick={() => setShowDeleteForm((s) => !s)}>
                <Trash2 size={16} /> Delete
              </button>
            )}
          </div>
        </div>

        {showDeleteForm && (
          <ReasonPrompt
            label="Delete Reason (optional) - this reverses the stock balance this return caused"
            confirmLabel="Delete Return"
            required={false}
            onConfirm={handleDelete}
            onCancel={() => setShowDeleteForm(false)}
          />
        )}

        {ret.isDeleted && (
          <div className="alert alert-danger">
            Deleted by {ret.deletedByName} on {new Date(ret.deletedDate).toLocaleString()}
            {ret.deleteReason ? `: ${ret.deleteReason}` : ""}
          </div>
        )}

        <div className="card mb-4">
          <div className="card-body">
            <div className="row mb-3">
              <div className="col-md-4"><strong>Reference:</strong> {ret.returnType === "STOCK_OUT_BASED" ? ret.stockOutNo : ret.supplierName}</div>
              <div className="col-md-4"><strong>Reason:</strong> {ret.returnReason || "-"}</div>
              <div className="col-md-4"><strong>Date:</strong> {new Date(ret.returnDate).toLocaleDateString()}</div>
              <div className="col-md-12 mt-2"><strong>Remarks:</strong> {ret.remarks}</div>
            </div>

            <div className="table-responsive">
              <table className="table table-bordered">
                <thead><tr><th>#</th><th>Product</th><th>Returned Qty</th><th>Unit</th><th>Rate</th><th>Amount</th><th>Remarks</th></tr></thead>
                <tbody>
                  {ret.lines.map((l, idx) => (
                    <tr key={l.lineId}>
                      <td>{idx + 1}</td>
                      <td>{l.productName}</td>
                      <td>{l.returnedQty}</td>
                      <td>{l.unit}</td>
                      <td>{l.unitRate != null ? Number(l.unitRate).toFixed(2) : "-"}</td>
                      <td>{l.lineAmount != null ? Number(l.lineAmount).toFixed(2) : "-"}</td>
                      <td>{l.remarks || "-"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="d-flex justify-content-end mt-2">
              <div>Total Amount: <strong>{Number(ret.totalAmount).toFixed(2)}</strong></div>
            </div>

            <div className="text-muted small mt-3">
              Created by <ActorName action="Created" name={ret.createdByName} /> on {new Date(ret.createdDate).toLocaleString()}
            </div>
          </div>
        </div>

        <StockDocumentPrintModal show={showPrint} onClose={() => setShowPrint(false)} docProps={printProps} copies={1} />
      </div>
    </div>
  );
};

export default StockReturnDetail;
